import React, { useState, useEffect, useRef } from 'react';
import './EmailTemplateModal.css';

const FONT_OPTIONS = ['Segoe UI', 'Arial', 'Aptos', 'Times New Roman', 'Calibri', 'Verdana'];
const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18];

// Extract the font a template body uses so the ribbon can reflect it
const detectFontFromHtml = (html) => {
    if (!html) return null;
    try {
        const container = document.createElement('div');
        container.innerHTML = html;
        let font = '';
        const styledEl = [...container.querySelectorAll('[style]')].find((el) => el.style.fontFamily);
        if (styledEl) font = styledEl.style.fontFamily;
        if (!font) {
            const fontEl = container.querySelector('font[face]');
            if (fontEl) font = fontEl.getAttribute('face') || '';
        }
        font = font.split(',')[0].trim().replace(/^["']+|["']+$/g, '');
        return font || null;
    } catch {
        return null;
    }
};

const EmailTemplateModal = ({ isOpen, onClose, onSave, editTemplate = null, hasPermission }) => { // 1. Accept hasPermission prop
    const [templateData, setTemplateData] = useState({
        template_name: '',
        subject: '',
        cc_emails: '',
        body: ''
    });

    const bodyRef = useRef(null);
    const modalRef = useRef(null);
    const savedRange = useRef(null);
    const [fontFamily, setFontFamily] = useState('Segoe UI');
    const [fontSize, setFontSize] = useState('');

    // 2. Determine if user has permission to edit/create
    const isEditing = !!editTemplate;
    const canEdit = isEditing ? hasPermission('EMAIL_CONFIG', 'can_edit') : hasPermission('EMAIL_CONFIG', 'can_create');

    // Initialize template data when editing
    useEffect(() => {
        if (editTemplate) {
            setTemplateData({
                id: editTemplate.id,
                template_name: editTemplate.template_name,
                subject: editTemplate.subject,
                cc_emails: editTemplate.cc_emails || '',
                body: editTemplate.body
            });
            // Ribbon font should reflect the template's own font
            setFontFamily(detectFontFromHtml(editTemplate.body) || 'Segoe UI');
        } else {
            setTemplateData({
                template_name: '',
                subject: '',
                cc_emails: '',
                body: ''
            });
            setFontFamily('Segoe UI');
        }
    }, [editTemplate]);

    // Initialize editor content only when modal/template context changes.
    // Rewriting innerHTML on every keypress resets caret and causes reversed typing.
    useEffect(() => {
        if (!isOpen || !bodyRef.current) return;
        bodyRef.current.innerHTML = editTemplate ? (editTemplate.body || '') : '';
    }, [isOpen, editTemplate]);

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // 3. Safeguard permission check
        if (!canEdit) {
            alert('You do not have permission to perform this action.');
            return;
        }

        if (!templateData.template_name.trim() || !templateData.subject.trim()) {
            alert('Please fill in template name and subject');
            return;
        }

        const bodyContent = bodyRef.current ? bodyRef.current.innerHTML : '';
        
        onSave({
            ...templateData,
            template_name: templateData.template_name.trim().toUpperCase(),
            subject: templateData.subject.trim().toUpperCase(),
            body: bodyContent
        });
    };

    // Handle body input changes
    const handleBodyInput = () => {
        if (bodyRef.current) {
            setTemplateData(prev => ({
                ...prev,
                body: bodyRef.current.innerHTML
            }));
        }
    };

    // Format text in body
    const formatText = (command, value = null) => {
        document.execCommand(command, false, value);
        handleBodyInput();
        bodyRef.current.focus();
    };

    // Selecting a toolbar dropdown/color picker moves focus out of the
    // editor and collapses the selection — capture it first, then restore
    // before running execCommand.
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel.rangeCount > 0 && bodyRef.current && bodyRef.current.contains(sel.anchorNode)) {
            savedRange.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        if (!savedRange.current) return;
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
    };

    const applyFontName = (font) => {
        setFontFamily(font);
        bodyRef.current.focus();
        restoreSelection();
        formatText('fontName', font);
    };

    const applyFontSize = (px) => {
        if (!bodyRef.current || !px) return;
        setFontSize(px);
        bodyRef.current.focus();
        restoreSelection();
        // execCommand('fontSize') only supports the legacy 1-7 scale, so
        // insert size 7 as a marker then rewrite it to a real pixel size.
        document.execCommand('styleWithCSS', false, false);
        document.execCommand('fontSize', false, '7');
        bodyRef.current.querySelectorAll('font[size="7"]').forEach((el) => {
            el.removeAttribute('size');
            el.style.fontSize = `${px}px`;
        });
        handleBodyInput();
    };

    const applyColor = (command, color) => {
        bodyRef.current.focus();
        restoreSelection();
        if (command === 'hiliteColor') {
            if (!document.execCommand('hiliteColor', false, color)) {
                document.execCommand('backColor', false, color);
            }
        } else {
            document.execCommand(command, false, color);
        }
        handleBodyInput();
    };

    // Handle click outside modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="email-template-modal-overlay">
            <div 
                ref={modalRef}
                className="email-template-modal-container"
            >
                <div className="email-template-modal-header">
                    <span className="email-template-modal-title">
                        {editTemplate ? 'Edit Email Template' : 'New Email Template'}
                    </span>
                    <button 
                        type="button"
                        onClick={onClose}
                        className="email-template-modal-close"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="email-template-modal-form">
                    <div className="email-template-modal-fields">
                        <div className="form-row">
                            <label className="form-label">Template Name:</label>
                            <input
                                type="text"
                                value={templateData.template_name}
                                onChange={(e) => setTemplateData(prev => ({ 
                                    ...prev, 
                                    template_name: e.target.value 
                                }))}
                                required
                                className="form-input"
                                placeholder="Enter template name"
                                readOnly={!canEdit} // 4. Make read-only
                            />
                        </div>

                        <div className="form-row">
                            <label className="form-label">Subject:</label>
                            <input
                                type="text"
                                value={templateData.subject}
                                onChange={(e) => setTemplateData(prev => ({ 
                                    ...prev, 
                                    subject: e.target.value 
                                }))}
                                required
                                className="form-input"
                                placeholder="Enter email subject"
                                readOnly={!canEdit} // 5. Make read-only
                            />
                        </div>

                        <div className="form-row">
                            <label className="form-label">CC (Optional):</label>
                            <input
                                type="text"
                                value={templateData.cc_emails || ''}
                                onChange={(e) => setTemplateData(prev => ({
                                    ...prev,
                                    cc_emails: e.target.value
                                }))}
                                className="form-input"
                                placeholder="cc1@example.com, cc2@example.com or use semicolon"
                                readOnly={!canEdit}
                            />
                        </div>

                        <div className="form-row body-row">
                            <label className="form-label">Body:</label>
                            
                            <div className="email-template-toolbar">
                                {/* 6. Disable all toolbar buttons */}
                                <button type="button" onClick={() => formatText('bold')} title="Bold" className="toolbar-btn" disabled={!canEdit}>
                                    <b>B</b>
                                </button>
                                <button type="button" onClick={() => formatText('italic')} title="Italic" className="toolbar-btn" disabled={!canEdit}>
                                    <i>I</i>
                                </button>
                                <button type="button" onClick={() => formatText('underline')} title="Underline" className="toolbar-btn" disabled={!canEdit}>
                                    <u>U</u>
                                </button>
                                <button type="button" onClick={() => formatText('insertUnorderedList')} title="Bullets" className="toolbar-btn" disabled={!canEdit}>
                                    •
                                </button>
                                <button type="button" onClick={() => formatText('insertOrderedList')} title="Numbered List" className="toolbar-btn" disabled={!canEdit}>
                                    1.
                                </button>
                                <button type="button" onClick={() => formatText('formatBlock', 'blockquote')} title="Quote" className="toolbar-btn" disabled={!canEdit}>
                                    "
                                </button>
                                <button type="button" onClick={() => formatText('removeFormat')} title="Clear Formatting" className="toolbar-btn" disabled={!canEdit}>
                                    ✗
                                </button>
                                <select
                                    className="toolbar-select"
                                    value={fontFamily}
                                    title="Font"
                                    disabled={!canEdit}
                                    onMouseDown={saveSelection}
                                    onChange={(e) => applyFontName(e.target.value)}
                                >
                                    {(FONT_OPTIONS.includes(fontFamily) ? FONT_OPTIONS : [fontFamily, ...FONT_OPTIONS]).map((f) => (
                                        <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                                    ))}
                                </select>
                                <select
                                    className="toolbar-select toolbar-select-size"
                                    value={fontSize}
                                    title="Font Size"
                                    disabled={!canEdit}
                                    onMouseDown={saveSelection}
                                    onChange={(e) => applyFontSize(e.target.value)}
                                >
                                    <option value="" disabled>Size</option>
                                    {FONT_SIZES.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                                <label className="toolbar-color-label" title="Font Colour" onMouseDown={saveSelection}>
                                    <span className="toolbar-color-icon" style={{ borderBottomColor: '#c00000' }}>A</span>
                                    <input
                                        type="color"
                                        className="toolbar-color-input"
                                        defaultValue="#000000"
                                        disabled={!canEdit}
                                        onChange={(e) => applyColor('foreColor', e.target.value)}
                                    />
                                </label>
                                <label className="toolbar-color-label" title="Text Highlight Colour" onMouseDown={saveSelection}>
                                    <span className="toolbar-color-icon" style={{ borderBottomColor: '#ffc000', background: '#fff3b0' }}>ab</span>
                                    <input
                                        type="color"
                                        className="toolbar-color-input"
                                        defaultValue="#ffff00"
                                        disabled={!canEdit}
                                        onChange={(e) => applyColor('hiliteColor', e.target.value)}
                                    />
                                </label>
                            </div>

                            <div
                                ref={bodyRef}
                                contentEditable={canEdit} // 7. Set contentEditable based on permission
                                className="email-template-body"
                                onInput={handleBodyInput}
                                onBlur={handleBodyInput}
                                suppressContentEditableWarning={true}
                                style={{
                                    direction: 'ltr',
                                    unicodeBidi: 'embed'
                                }}
                            />
                        </div>
                    </div>

                    <div className="email-template-modal-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                        >
                            Cancel
                        </button>
                        {/* 8. Conditionally render the submit button */}
                        {canEdit && (
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {editTemplate ? 'Update Template' : 'Save Template'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EmailTemplateModal;